import * as React from 'react';
import { useMemo, useState } from 'react';

import { ColumnInstance, Row } from 'react-table';

import { Option } from '@components/common';
import { DropdownLink } from '@components/common';
import { BaseCsvModalComponent } from '@components/shared/ExportCsvModal';
import { StudyAction } from '@components/StudiesApp/components/StudyPublished/components/ACTIONS';
import { BulkActions } from '@components/StudiesApp/components/StudyPublished/components/BulkActions';
import { DefaultActions } from '@components/StudiesApp/components/StudyPublished/components/DefaultActions';
import { LockSVG } from '@components/svgs';
import { useDisabledFeatures } from '@hooks/useDisabledFeatures';
import { usePermission } from '@hooks/usePermission';
import { api } from 'api/reduxApi';

import { ParticipationTableItem } from '../../../ParticipationTable/helpers/buildParticipantsColumns';

interface Props {
  currentStatus: ParticipationStatus;
  selectedIds: number[];
  study: Study;
  mode: StudyAction | null;
  setMode: (v: StudyAction | null) => void;
  participations: Participation[];
  closeAll: () => void;
  setLoading: (v: boolean) => void;
  handleParticipationsUpdate: (backgroundTask?: BackgroundTask) => void;
  columnOptions: Option[];
  visibleColumnNames: string[];
  visibleColumns: ColumnInstance<ParticipationTableItem>[];
  studyLimits?: StudyLimit[];
  setAddCandidatesDefaultStudyLimit: (v: StudyLimit | null) => void;
  rows: Row<ParticipationTableItem>[];
  query?: ServerFilterQuery;
  allSelected?: boolean;
}

export const ParticipantsActions: React.FC<React.PropsWithChildren<Props>> = ({
  currentStatus,
  selectedIds,
  study,
  mode,
  setMode,
  participations,
  closeAll,
  setLoading,
  handleParticipationsUpdate,
  columnOptions,
  visibleColumnNames,
  studyLimits,
  setAddCandidatesDefaultStudyLimit,
  rows,
  query,
  allSelected
}) => {
  const { disabledFeatures } = useDisabledFeatures();
  const canUpdate = usePermission<Study>('updateStudy')(study);
  const canExportCsv = usePermission('exportCsv')() && !disabledFeatures.exporting_candidates_via_csv;
  const canExportCsvTooltip: string = disabledFeatures.exporting_candidates_via_csv
    ? 'The admin has restricted the ability to export candidates.'
    : 'Available only for admins';
  const csvHeaders = useMemo(() => {
    let filteredColumns = columnOptions
      .filter(({ value }) => visibleColumnNames.includes(value))
      .map(({ label, value }) => ({ label, key: value }));

    // NOTE(Caio): Make sure the customer name is always the first column.
    // If it is somewhere else, remove it from the array and add it to the beginning.
    // We need this because the customer name is being listed as 'name' and not 'customer:name'
    // on visibleColumnNames due to legacy (pre server-side participations) reasons.
    // When we have server-side participations rolled out to all customers, we can fully rename this
    // column to 'customer:name' and remove this logic.
    const nameColumn = { label: 'Name', key: 'customer:name' };
    filteredColumns = filteredColumns.filter((col) => col.key !== 'customer:name');
    return [nameColumn, ...filteredColumns];
  }, [columnOptions, visibleColumnNames]);

  const csvData = useMemo(() => rows.map((row) => row.values), [rows]);
  const [csvExportModalOpen, setCsvExportModalOpen] = useState<boolean>(false);
  const [createCsvExport, { isSuccess, isError, isLoading }] = api.useCreateParticipationCsvExportMutation();
  const params: ParticipationCsvExportParams = {
    columns: csvHeaders,
    kind: 'participations',
    study_id: study.id,
    ...(allSelected ? { query } : { ids: selectedIds })
  };

  const handleCsvExport = async () => {
    await createCsvExport(params).unwrap();

    setCsvExportModalOpen(false);
  };

  return (
    <>
      {!['closed', 'archived'].includes(study.state) &&
        ((selectedIds.length > 0 || allSelected) && canUpdate ? (
          <BulkActions
            query={query}
            allSelected={allSelected}
            medium
            selectedIds={selectedIds}
            study={study}
            currentStatus={currentStatus}
            mode={mode}
            participations={participations}
            onSuccess={handleParticipationsUpdate}
            closeAll={closeAll}
            setLoading={setLoading}
            setMode={setMode}
            renderExportCSV={() => (
              <DropdownLink
                disabled={!canExportCsv}
                disabledTooltip={canExportCsvTooltip}
                className='flex flex-nowrap items-center'
                onClick={() => setCsvExportModalOpen(true)}
              >
                Export CSV
                {!canExportCsv ? <LockSVG className='ml-2' /> : null}
              </DropdownLink>
            )}
          />
        ) : (
          <DefaultActions
            renderExportCSV={() => (
              <DropdownLink
                disabled={!canExportCsv}
                disabledTooltip={canExportCsvTooltip}
                className='flex flex-nowrap items-center'
                onClick={() => setCsvExportModalOpen(true)}
              >
                Export CSV
                {!canExportCsv ? <LockSVG className='ml-2' /> : null}
              </DropdownLink>
            )}
            canUpdate={canUpdate}
            setMode={setMode}
            mode={mode}
            study={study}
            closeAll={closeAll}
            studyLimits={studyLimits || []}
            setStudyLimit={setAddCandidatesDefaultStudyLimit}
          />
        ))}

      {csvExportModalOpen && (
        <BaseCsvModalComponent
          isSuccess={isSuccess}
          isError={isError}
          isLoading={isLoading}
          setOpen={setCsvExportModalOpen}
          handleCsvExport={handleCsvExport}
          subjectString={'participation data'}
        />
      )}
    </>
  );
};
